import React, { useEffect, useMemo, useState } from 'react';
import { Form, Typography, Button, Card, Divider, message } from 'antd';
import { SaveOutlined, GlobalOutlined } from '@ant-design/icons';
import CommonForm from '../../../global/form';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import SOURCES from '../../../../utils/constants';
import { cn } from '../../../../utils/cn';
import Spinner from '../../../global/spinner';

const { Title, Text } = Typography;

const EnhancedCheckCherry = ({ destinations, activeDestination, btnClassName }) => {
    const [form] = Form.useForm();
    const DESTINATION_CHECK_CHERRY = SOURCES.DESTINATION_CHECK_CHERRY || "checkcherry";
    const [fixedData, setFixedData] = useState({})

    const { mutate: saveCheckCherry, isPending: isSaving } = useGenericMutation({
        endpoint: "integration",
        method: "POST",
        onSuccess: () => message.success("Check Cherry configuration saved successfully")
    });


    useEffect(() => {
        if (destinations) {
            const config = destinations.find(d => d.integrationCompany === DESTINATION_CHECK_CHERRY);
            if (config) {
                form.setFieldsValue({
                    checkCherryApiKey: config.destinationAccountPassword,
                    checkCherryAppId: config.publicKey,
                    checkCherryAppSecret: config.secretKey,
                    sendLeadsLimit: config.leadSentConfig?.sentLimit || 0
                });
                setFixedData({
                    destinationAccountEmail: config.destinationAccountEmail,
                    destinationAccountPassword: config.destinationAccountPassword,
                    agentProvider: config.agentProvider,
                    locations: config.locations,
                })
            }
        }
    }, [destinations, form]);

    const onFinish = (values) => {
        const finalizedData = {
            publicKey: values.checkCherryAppId,
            secretKey: values.checkCherryAppSecret,
            destinationAccountPassword: fixedData.destinationAccountPassword,
            sendLeadsLimit: values.sendLeadsLimit,
            destinationAccountEmail: fixedData.destinationAccountEmail,
            integrationCompany: DESTINATION_CHECK_CHERRY,
            agentProvider: fixedData.agentProvider,
            locations: fixedData.locations,
        }
        console.log(finalizedData)
        saveCheckCherry(finalizedData);
    };

    const formData = useMemo(() => [
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "App Id",
            name: "checkCherryAppId",
            placeholder: "Get APP ID from Check Cherry",
            required: true,
            span: 12,
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "App Secret",
            name: "checkCherryAppSecret",
            placeholder: "Get APP SECRET from Check Cherry",
            required: true,
            span: 12,
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Initial Lead Send Limit",
            name: "sendLeadsLimit",
            placeholder: "Total lead(s) send initially",
            type: "number",
            span: 24,
        }
    ], []);

    return (
        <div className="animate-in fade-in duration-500">
            <div className="flex items-center gap-4 mb-8">
                <div className="size-16 rounded-2xl bg-white shadow-sm border border-gray-100 flex items-center justify-center p-3">
                    <img src={activeDestination.icon} alt={activeDestination.title} className="w-full h-full object-contain" />
                </div>
                <div>
                    <Title level={3} className="!mb-1">{activeDestination.title} Configuration</Title>
                    <Text type="secondary">Manage your Check Cherry integration settings.</Text>
                </div>
            </div>

            <div className="space-y-6">
                <div className="bg-primary/10 p-3 rounded-xl">
                    <div className="flex items-center gap-3">
                        <GlobalOutlined className="text-primary mt-0" />
                        <Text className="text-sm text-primary">
                            <Text strong className="text-primary">NOTE:</Text> You can find your API Private Key by navigating to <Text strong className="text-primary">Manage → Website Integration</Text> from your dashboard.
                            To get APP ID and APP Secret, please contact Check Cherry at{' '}
                            <a href="mailto:support@checkcherry.com" className="text-primary hover:underline font-medium">support@checkcherry.com</a>.
                        </Text>
                    </div>
                </div>

                <Card className="rounded-2xl border-none shadow-sm">
                    <CommonForm
                        form={form}
                        data={formData}
                        onFinish={onFinish}
                        layoutType="vertical"
                    />
                    <Divider className="mb-4 border-gray-100" />
                    <div className="flex justify-start">
                        <Button
                            size="large"
                            icon={isSaving ? null : <SaveOutlined />}
                            onClick={() => form.submit()}
                            className={cn(btnClassName)}
                        >
                            {isSaving && <Spinner className={"!border-white"} />}
                            Save Configuration
                        </Button>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default EnhancedCheckCherry;
